/* SIZESOFTX
=========================================================================================
* Descripción: Cliente de manuales vía backend Support (proxy a n8n VPS2).
*              El navegador NUNCA llama a n8n: evita CORS y tokens en el bundle.
*              El backend (SupportManualesController, rol SA) reenvía a n8n con
*              X-SizeSoftX-Secret del servidor e impone tenant_id/user_id del JWT.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 08/10/2026 | Muse Spark                       | Proxy backend anti-CORS/token navegador.
* ---------------------------------------------------------------------------------------
*/

import supportApi, { unwrap } from "./ApiConfig";
import { getSessionUser } from "./supportSession";

const supportDebugEnabled = process.env.REACT_APP_N8N_SUPPORT_DEBUG === "true";

const sanitizeForConsole = (value, key = "") => {
  if (/token|authorization|password|secret/i.test(key)) return "[oculto]";
  if (typeof value === "string" && /base64/i.test(key)) {
    return `[base64 omitido: ${value.length.toLocaleString()} caracteres]`;
  }
  if (Array.isArray(value)) return value.map(item => sanitizeForConsole(item));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([entryKey, entryValue]) => [
      entryKey,
      sanitizeForConsole(entryValue, entryKey),
    ]));
  }
  return value;
};

export const supportDebugLog = (event, details = {}, level = "info") => {
  if (!supportDebugEnabled || typeof console === "undefined") return;
  const log = console[level] || console.log;
  log.call(console, `[Support manuales] ${event}`, sanitizeForConsole(details));
};

const manualPath = "/SupportManuales";

const getSupportUserId = () => {
  const user = getSessionUser() || {};
  return String(user.sEmail || user.email || user.sUsuario || user.userName || user.username || "support-master");
};

const normalizeSlug = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

// n8n hace upsert por slug o actualización por manual_id.
// Si se edita sin enviar imágenes (images es undefined), se omite para conservar las imágenes en BD.
const buildGuardarPayload = (payload = {}) => {
  const manualId = payload.manual_id || payload.manualId || payload.id;
  const result = {
    slug: normalizeSlug(payload.slug),
    title: String(payload.title || "").trim(),
    topic: String(payload.topic || "").trim(),
    version: String(payload.version || "1.0").trim(),
    content_markdown: String(payload.content_markdown ?? ""),
    created_by: String(payload.created_by || "Support"),
    modified_by: String(payload.modified_by || "Support"),
  };
  if (manualId) {
    result.manual_id = String(manualId);
  }
  if (Array.isArray(payload.images)) {
    result.images = payload.images;
  }
  return result;
};

export const apiManualesSupportList = async () => {
  const data = await supportApi.get(manualPath).then(unwrap);
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.manuales)
        ? data.manuales
        : Array.isArray(data?.result)
          ? data.result
          : [];
  return { items };
};

export const apiManualSupportDetalle = async (id) => {
  const manual = await supportApi.get(`${manualPath}/${encodeURIComponent(id)}`).then(unwrap);
  if (!manual?.manual_id) throw new Error("El backend no encontró el manual solicitado.");
  return manual;
};

export const apiManualSupportGuardar = (payload) =>
  supportApi.post(manualPath, buildGuardarPayload(payload), { timeout: 120000 }).then(unwrap);

export const apiManualSupportActualizar = (id, payload) =>
  supportApi.post(manualPath, buildGuardarPayload({ ...payload, manual_id: id }), { timeout: 120000 }).then(unwrap);

export const apiManualSupportEliminar = async (id) => {
  const result = await supportApi.delete(`${manualPath}/${encodeURIComponent(id)}`).then(unwrap);
  if (result?.deleted === false) throw new Error("El backend no encontró el manual que se quería eliminar.");
  return result;
};

export const apiManualSupportPublicar = (id) =>
  supportApi.post(`${manualPath}/${encodeURIComponent(id)}/publicar`, {}).then(unwrap);

export const apiManualSupportImagen = async (id, imageId) => {
  const image = await supportApi
    .get(`${manualPath}/${encodeURIComponent(id)}/imagenes/${encodeURIComponent(imageId)}`, { timeout: 120000 })
    .then(unwrap);
  if (!image?.data_base64) throw new Error("El backend no devolvió los bytes de la imagen.");
  const binary = window.atob(image.data_base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return URL.createObjectURL(new Blob([bytes], { type: image.mime_type || "application/octet-stream" }));
};

export const apiManualSupportConocimientoIniciar = (id, payload) =>
  supportApi.post(
    `${manualPath}/${encodeURIComponent(id)}/test/jobs`,
    {
      conversation_id: payload?.conversation_id,
      message: payload?.message,
    },
    { timeout: 60000 }
  ).then(unwrap);

export const apiManualSupportConocimientoEstado = (jobId) =>
  supportApi
    .get(`${manualPath}/test/jobs/${encodeURIComponent(jobId)}`, { timeout: 60000 })
    .then(unwrap);

export const apiManualSupportCerrarConversacion = (conversationId) =>
  supportApi.post(`${manualPath}/test/conversaciones/${encodeURIComponent(conversationId)}/cerrar`, {}, { timeout: 60000 }).then(unwrap);

export const apiSupportAgentConfig = () =>
  supportApi.get(`${manualPath}/agent/config`).then(unwrap);

export const apiSupportAgentConfigGuardar = (payload) =>
  supportApi.put(`${manualPath}/agent/config`, payload ?? {}).then(unwrap);
